import { fmt, fmtQty, type PurchaseJournal } from '@k2pos/core';
import { useNavigate } from 'react-router-dom';
import { HazardStripes } from '@/components/brand/HazardStripes';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Mono, Text } from '@/components/ui/Text';
import { IconCheck } from '@/components/icons';
import { usePos } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './PurchaseDone.module.css';

/**
 * Confirmation for goods received.
 *
 * Reads the newest purchase off the journal rather than a `lastPurchase` field:
 * the sale-done screen taught us that a render-once handoff kept in state comes
 * back on a cold start looking like a duplicate.
 */
export function PurchaseDone() {
  const navigate = useNavigate();
  const catalog = usePos((s) => s.catalog);
  const purchase = usePos((s) =>
    [...s.journal].reverse().find((e): e is PurchaseJournal => e.kind === 'purchase'),
  );

  if (!purchase) {
    return (
      <div className={styles.centered}>
        <Text size={13} color={colors.textMuted}>
          ບໍ່ມີການຮັບເຄື່ອງລ່າສຸດ
        </Text>
        <Button
          label="ກັບໜ້າຫຼັກ"
          className={styles.wide}
          onPress={() => navigate('/', { replace: true })}
        />
      </div>
    );
  }

  return (
    <>
      <div className={styles.success}>
        <HazardStripes className={styles.stripes} opacity={0.3} period={18} />
        <div className={styles.check}>
          <IconCheck size={26} />
        </div>
        <Text weight={800} size={17} color={colors.onDark}>
          ຮັບເຄື່ອງເຂົ້າແລ້ວ
        </Text>
        <Text size={11} color={colors.onDarkSoft}>
          {purchase.supplier} · {fmt(purchase.total)} ₭
        </Text>
      </div>

      <div className={`${styles.body} vscroll`}>
        <Card elevated className={styles.card}>
          <Text weight={800} size={13}>
            ສະຕັອກຫຼັງຮັບເຄື່ອງ
          </Text>
          {purchase.lines.map((line) => {
            const product = catalog.find((p) => p.id === line.productId);
            return (
              <div key={line.productId} className={styles.row}>
                <div className="grow col">
                  <Text size={12.5} lines={1}>
                    {line.name}
                  </Text>
                  <Mono size={10.5} color={colors.textFaint}>
                    +{fmtQty(line.qty)} {line.unit}
                    {line.unit !== product?.units[0]?.unit
                      ? ` = ${fmtQty(line.qtyBase)} ${product?.units[0]?.unit ?? ''}`
                      : ''}
                  </Mono>
                </div>
                <Mono weight={700} size={13}>
                  {product?.remaining === undefined ? '—' : fmtQty(product.remaining)}
                </Mono>
              </div>
            );
          })}
        </Card>

        {/* Cost is a moving average, so the shop should see where it landed —
            it is what every future margin will be measured against. */}
        <Card className={styles.card}>
          <Text weight={800} size={13}>
            ຕົ້ນທຶນສະເລ່ຍໃໝ່
          </Text>
          {purchase.lines.map((line) => {
            const product = catalog.find((p) => p.id === line.productId);
            return (
              <div key={line.productId} className={styles.row}>
                <Text size={12.5} lines={1} className="grow">
                  {line.name}
                </Text>
                <Mono weight={700} size={13} color={colors.skyDeep}>
                  {product?.cost === undefined ? '—' : `${fmt(product.cost)} ₭`}
                </Mono>
              </div>
            );
          })}
          <Text size={10} color={colors.textFaint}>
            ຄິດແບບສະເລ່ຍຖ່ວງນ້ຳໜັກ · ບິນທີ່ຂາຍໄປແລ້ວບໍ່ປ່ຽນ
          </Text>
        </Card>

        <div className={styles.actions}>
          <Button
            label="ຮັບເຄື່ອງອີກ"
            variant="outline"
            className="grow"
            onPress={() => navigate('/purchase', { replace: true })}
          />
          <Button
            label="ກັບໜ້າຫຼັກ"
            className="grow"
            onPress={() => navigate('/', { replace: true })}
          />
        </div>
      </div>
    </>
  );
}
